
import React, { useState } from 'react';
import { ZodiacSign } from '../types';
import { getHoroscope } from '../services/geminiService';
import { useClient } from '../contexts/ClientContext';
import { DivinationBlurLock } from './DivinationBlurLock';
import { ZodiacIcon, SparklesIcon } from './Icons';

// Données d'affichage (Symboles & Dates)
const ZODIAC_DATA: Record<string, { dates: string }> = {
    [ZodiacSign.Aries]: { dates: "21 Mar - 19 Avr" },
    [ZodiacSign.Taurus]: { dates: "20 Avr - 20 Mai" },
    [ZodiacSign.Gemini]: { dates: "21 Mai - 20 Juin" },
    [ZodiacSign.Cancer]: { dates: "21 Juin - 22 Juil" },
    [ZodiacSign.Leo]: { dates: "23 Juil - 22 Août" },
    [ZodiacSign.Virgo]: { dates: "23 Août - 22 Sep" },
    [ZodiacSign.Libra]: { dates: "23 Sep - 22 Oct" },
    [ZodiacSign.Scorpio]: { dates: "23 Oct - 21 Nov" },
    [ZodiacSign.Sagittarius]: { dates: "22 Nov - 21 Déc" },
    [ZodiacSign.Capricorn]: { dates: "22 Déc - 19 Jan" },
    [ZodiacSign.Aquarius]: { dates: "20 Jan - 18 Fév" },
    [ZodiacSign.Pisces]: { dates: "19 Fév - 20 Mar" },
};

const Horoscope: React.FC = () => {
    const { client, recordDivinationActivity } = useClient();
    const [selectedSign, setSelectedSign] = useState<string | null>(null);
    const [horoscopeText, setHoroscopeText] = useState<string>("");
    const [loading, setLoading] = useState(false);

    const handleSignSelect = async (sign: string) => {
        setSelectedSign(sign);
        setLoading(true);
        const text = await getHoroscope(sign);
        setHoroscopeText(text);
        if (client && text) {
            recordDivinationActivity('horoscope', 'Horoscope du Jour', `Horoscope du signe ${sign}`, { selectedSign: sign, text });
        }
        setLoading(false);
    };

    return (
        <section className="py-10 bg-transparent border-t border-white/5 min-h-screen">
            <div className="container mx-auto px-4">
                <div className="text-center mb-12">
                    <h3 className="text-3xl font-serif font-bold text-white mb-2">Horoscope du Jour</h3>
                    <p className="text-mystic-100">Sélectionnez votre signe pour découvrir votre climat astral.</p>
                </div>

                <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-10 max-w-5xl mx-auto">
                    {Object.entries(ZodiacSign).map(([key, value]) => {
                        const info = ZODIAC_DATA[value];
                        return (
                            <button
                                key={key}
                                onClick={() => handleSignSelect(value)}
                                className={`group relative p-2 rounded-xl border transition-all duration-300 flex flex-col items-center justify-center h-32 md:h-36 ${
                                    selectedSign === value 
                                    ? 'bg-purple-900/50 border-gold-400 shadow-[0_0_20px_rgba(251,191,36,0.3)] transform scale-105' 
                                    : 'bg-black/40 border-mystic-700 hover:border-purple-500 hover:bg-black/60 hover:-translate-y-1'
                                }`}
                            >
                                <div className={`mb-2 transition-colors duration-300 ${selectedSign === value ? 'text-gold-400' : 'text-purple-300 group-hover:text-white'}`}><ZodiacIcon sign={value} className="w-8 h-8 md:w-10 md:h-10" /></div>
                                <span className={`block font-serif font-bold text-xs md:text-sm uppercase ${selectedSign === value ? 'text-white' : 'text-gray-300 group-hover:text-gold-400'}`}>
                                    {value}
                                </span>
                                <span className="text-[9px] text-gray-500 mt-1 opacity-70 group-hover:opacity-100 transition-opacity">
                                    {info.dates}
                                </span>
                            </button>
                        );
                    })}
                </div>

                {selectedSign && (
                    <div className="max-w-2xl mx-auto glass-panel p-8 rounded-2xl relative min-h-[220px] flex items-center justify-center animate-fade-in-up border border-gold-500/20 shadow-2xl bg-black/60 overflow-hidden">
                        {/* SI NON CONNECTÉ -> CADENAS & OVERLAY FLOUTÉ */}
                        {!loading && !client && (
                            <DivinationBlurLock 
                                title={`Horoscope du ${selectedSign} Prêt`}
                                subtitle="Votre climat astral complet, vos prévisions d'amour, travail et bien-être sont disponibles. Connectez-vous ou créez votre compte gratuit en 10 secondes pour dévoiler votre horoscope complet."
                            />
                        )}

                        {loading ? (
                            <div className="flex flex-col items-center gap-3 py-8">
                                <SparklesIcon className="w-10 h-10 text-gold-400 animate-spin" />
                                <span className="text-sm text-purple-200 animate-pulse">Consultation des astres...</span>
                            </div>
                        ) : (
                            <div className={`text-center relative z-10 w-full ${!client ? 'filter blur-sm select-none pointer-events-none opacity-40' : ''}`}>
                                <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-12 bg-black/80 w-16 h-16 flex items-center justify-center rounded-full border border-gold-500/30 shadow-lg">
                                     <ZodiacIcon sign={selectedSign} className="w-8 h-8 text-gold-400" />
                                </div>
                                <h4 className="text-3xl font-serif text-gold-400 mb-6 mt-4">{selectedSign}</h4>
                                <div className="prose prose-invert">
                                    <p className="text-gray-200 leading-relaxed text-lg font-light mb-8 whitespace-pre-line">
                                        {typeof horoscopeText === 'string' ? horoscopeText : (typeof horoscopeText === 'object' ? Object.entries(horoscopeText).map(([k,v]) => `${k}: ${v}`).join('\n') : String(horoscopeText || ''))}
                                    </p>
                                </div>
                                <div className="border-t border-white/10 pt-6">
                                    <p className="text-sm text-gray-400 mb-3">Besoin d'approfondir ces prédictions ?</p>
                                    <a href="tel:0985405274" className="inline-flex items-center gap-2 bg-gradient-to-r from-purple-700 to-indigo-700 hover:from-purple-600 hover:to-indigo-600 px-8 py-3 rounded-full text-white font-bold shadow-lg transition-transform hover:scale-105">
                                        Parler à un astrologue au 09 85 40 52 74
                                    </a>
                                </div>
                            </div>
                        )}
                        <div className="absolute inset-0 bg-gradient-to-b from-purple-500/5 to-transparent pointer-events-none rounded-2xl"></div>
                    </div>
                )}
            </div>
        </section>
    );
};

export default Horoscope;
